import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  MoreVert,
  Person,
  AdminPanelSettings,
  Block,
  CheckCircle,
  Refresh,
  Download,
  FilterList,
  TrendingUp,
  People,
  Security,
  Schedule,
  Warning,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Email,
  Close,
  MailOutline,
  Group,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { adminApi } from '../../services/adminApi';
import AdminTeamManagement from './AdminTeamManagement';

// Stats Card Component
const StatsCard = ({ title, value, icon, color, trend, subtitle }) => (
  <Card
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: '1px solid',
      borderColor: `${color}30`,
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ my: 1, color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color }}>
          {icon}
        </Avatar>
      </Stack>
      {trend && (
        <Chip
          size="small"
          icon={<TrendingUp sx={{ fontSize: 14 }} />}
          label={trend}
          sx={{ mt: 1, bgcolor: 'success.lighter', color: 'success.main' }}
        />
      )}
    </CardContent>
  </Card>
);

// User Row Actions Menu
const UserActionsMenu = ({ user, onEdit, onDelete, onToggleStatus, onResetPassword }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { onEdit(user); setAnchorEl(null); }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onResetPassword(user); setAnchorEl(null); }}>
          <ListItemIcon><Security fontSize="small" /></ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onToggleStatus(user); setAnchorEl(null); }}>
          <ListItemIcon>
            {user.is_active ? <Block fontSize="small" color="error" /> : <CheckCircle fontSize="small" color="success" />}
          </ListItemIcon>
          <ListItemText>{user.is_active ? 'Disable Account' : 'Enable Account'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { onDelete(user); setAnchorEl(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const AdminPortal = () => {
  const { isAuthenticated, user, token, logout, isLoading } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [trackingProvider, setTrackingProvider] = useState('none');
  const [trackingIntegration, setTrackingIntegration] = useState({
    enabled: false,
    accountEmail: '',
    dashboardUrl: 'https://mailsuite.com',
    sendWebhookUrl: '',
    notes: '',
    webhookSecret: '',
  });
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resellerFilter, setResellerFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [forceLogoutDialogOpen, setForceLogoutDialogOpen] = useState(false);
  const [activateSubscriptionDialogOpen, setActivateSubscriptionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    price: '',
    paymentType: '',
    durationMonths: 1,
    resellerName: '',
  });
  
  // Form states
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    quota_per_day: 250,
    role: 'user',
  });
  const [editForm, setEditForm] = useState({
    email: '',
    quota_per_day: 250,
    role: 'user',
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const trackingWebhookUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const secret = trackingIntegration.webhookSecret || '';
    const base = window.location.origin || '';
    if (!base) return '';
    return `${base}/api/external_tracking.php${secret ? `?secret=${encodeURIComponent(secret)}` : ''}`;
  }, [trackingIntegration.webhookSecret]);

  // Get subscription status display
  const getSubscriptionStatus = (user) => {
    if (!user.subscription_end_date) return { status: 'trial', color: '#f59e0b', label: 'Trial' };
    
    const endDate = new Date(user.subscription_end_date);
    const today = new Date();
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 0) return { status: 'expired', color: '#ef4444', label: 'Expired' };
    if (user.subscription_status === 'active') return { status: 'active', color: '#10b981', label: 'Active', daysRemaining };
    
    return { status: 'trial', color: '#f59e0b', label: 'Trial' };
  };

  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Check if user is online (active in last 5 minutes)
  const isUserOnline = (userId) => {
    return sessions.some(s => {
      if (s.user_id !== userId) return false;
      const lastActivity = new Date(s.last_activity || s.last_seen);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      return lastActivity > fiveMinutesAgo;
    });
  };

  // Get user sessions
  const getUserSessions = (userId) => {
    return sessions.filter(s => s.user_id === userId);
  };

  const usageMap = useMemo(() => {
    const map = new Map();
    usage.forEach((row) => map.set(row.id, row));
    return map;
  }, [usage]);

  // Get unique resellers for filter
  const uniqueResellers = useMemo(() => {
    const resellers = new Set();
    users.forEach(u => {
      if (u.reseller_name) resellers.add(u.reseller_name);
    });
    return Array.from(resellers).sort();
  }, [users]);

  // Filter users based on search and reseller
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    // Filter by reseller
    if (resellerFilter !== 'all') {
      if (resellerFilter === 'none') {
        filtered = filtered.filter(u => !u.reseller_name);
      } else {
        filtered = filtered.filter(u => u.reseller_name === resellerFilter);
      }
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.email?.toLowerCase().includes(query) ||
          u.role?.toLowerCase().includes(query) ||
          u.reseller_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [users, searchQuery, resellerFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const activeUsers = users.filter((u) => u.is_active).length;
    const adminCount = users.filter((u) => u.role === 'admin').length;
    const totalSearchesToday = usage.reduce((sum, u) => sum + (u.today_usage || 0), 0);
    const activeSessions = sessions.filter((s) => {
      const lastSeen = new Date(s.last_activity || s.last_seen);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      return lastSeen > hourAgo;
    }).length;
    const onlineUsers = new Set(sessions.filter(s => {
      const lastActivity = new Date(s.last_activity || s.last_seen);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      return lastActivity > fiveMinutesAgo;
    }).map(s => s.user_id)).size;
    const suspiciousSessions = sessions.filter((s) => s.suspicious).length;
    
    // Subscription stats
    const trialUsers = users.filter(u => !u.subscription_status || u.subscription_status === 'trial').length;
    const activeSubscriptions = users.filter(u => u.subscription_status === 'active').length;
    const expiredSubscriptions = users.filter(u => u.subscription_status === 'expired').length;
    const totalRevenue = users.reduce((sum, u) => sum + (u.subscription_price || 0), 0);
    
    // Reseller stats
    const resellerCounts = {};
    users.forEach(u => {
      if (u.reseller_name) {
        resellerCounts[u.reseller_name] = (resellerCounts[u.reseller_name] || 0) + 1;
      }
    });
    const uniqueResellers = Object.keys(resellerCounts).length;

    // SMTP Accounts count (total email_accounts for all users)
    const totalSmtpAccounts = users.reduce((sum, u) => sum + (u.email_accounts_count || 0), 0);

    return { 
      totalUsers, 
      activeUsers, 
      adminCount, 
      totalSearchesToday, 
      activeSessions, 
      onlineUsers, 
      suspiciousSessions, 
      trialUsers, 
      activeSubscriptions, 
      expiredSubscriptions,
      totalRevenue,
      uniqueResellers,
      resellerCounts,
      totalSmtpAccounts
    };
  }, [users, usage, sessions]);

  const loadTrackingPrefs = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const res = await adminApi.loadPrefs(token);
      if (typeof res?.prefs?.trackingProvider === 'string') {
        setTrackingProvider(res.prefs.trackingProvider);
      }
      if (res?.prefs?.trackingIntegration && typeof res.prefs.trackingIntegration === 'object') {
        setTrackingIntegration((prev) => ({
          ...prev,
          ...res.prefs.trackingIntegration,
          dashboardUrl: res.prefs.trackingIntegration.dashboardUrl || prev.dashboardUrl,
        }));
      }
    } catch (err) {
      console.warn('Failed to load tracking integration prefs', err);
    }
  }, [isAuthenticated, token]);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    setLoading(true);
    setError('');
    try {
      const [usersRes, sessionsRes, usageRes] = await Promise.all([
        adminApi.listUsers(token),
        adminApi.listSessions(token),
        adminApi.getUsage(token),
      ]);
      setUsers(usersRes.users || []);
      setSessions(sessionsRes.sessions || []);
      setUsage(usageRes.usage || []);
      await loadTrackingPrefs();
    } catch (err) {
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, loadTrackingPrefs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Redirect to landing page if not authenticated or not admin
  useEffect(() => {
    if (isLoading) return; // Wait for auth to load
    
    if (!isAuthenticated || (user && user.role !== 'admin')) {
      console.log('🔒 Access denied, redirecting to home page...');
      window.location.href = '/';
    }
  }, [isLoading, isAuthenticated, user]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchData]);

  const handleSaveTrackingIntegration = async () => {
    if (!token) return;
    setTrackingSaving(true);
    setError('');
    try {
      const currentPrefs = await adminApi.loadPrefs(token).catch(() => ({ prefs: {} }));
      await adminApi.savePrefs(
        {
          ...(currentPrefs?.prefs || {}),
          trackingProvider,
          trackingIntegration,
        },
        token
      );
      toast.success('Tracking integration settings saved.', { title: 'Tracking Updated' });
    } catch (err) {
      const message = err.message || 'Unable to save tracking integration';
      toast.error(message);
      setError(message);
    } finally {
      setTrackingSaving(false);
    }
  };

  const generateTrackingWebhookSecret = () => {
    const next = `mt_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    setTrackingIntegration((prev) => ({ ...prev, webhookSecret: next }));
  };

  const handleCreateUser = async () => {
    setFormLoading(true);
    setError('');
    try {
      await adminApi.createUser(
        {
          email: createForm.email,
          password: createForm.password,
          quota_per_day: Number(createForm.quota_per_day),
          role: createForm.role,
        },
        token
      );
      toast.success('User created successfully!', { title: 'User Created' });
      setCreateDialogOpen(false);
      setCreateForm({ email: '', password: '', quota_per_day: 250, role: 'user' });
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Unable to create user');
      setError(err.message || 'Unable to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    setError('');
    try {
      await adminApi.updateUser(selectedUser.id, editForm, token);
      toast.success('User updated successfully!', { title: 'User Updated' });
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Unable to update user');
      setError(err.message || 'Unable to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    setError('');
    try {
      await adminApi.deleteUser(selectedUser.id, token);
      toast.success('User deleted successfully!', { title: 'User Deleted' });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Unable to delete user');
      setError(err.message || 'Unable to delete user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (userToToggle) => {
    try {
      await adminApi.updateUser(userToToggle.id, { is_active: !userToToggle.is_active }, token);
      toast.success(`User ${userToToggle.is_active ? 'disabled' : 'enabled'} successfully!`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Unable to update user status');
      setError(err.message || 'Unable to update user status');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setFormLoading(true);
    setError('');
    try {
      await adminApi.resetUserPassword(selectedUser.id, newPassword, token);
      toast.success('Password reset successfully!', { title: 'Password Reset' });
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.message || 'Unable to reset password');
      setError(err.message || 'Unable to reset password');
    } finally {
      setFormLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let pass = '';
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pass);
  };

  const handleForceLogout = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    setError('');
    try {
      // Use the new API endpoint to logout user from all devices
      await adminApi.logoutUserAllDevices(selectedUser.id, token);
      toast.success(`${selectedUser.email} has been logged out from all devices!`, { title: 'Force Logout' });
      setForceLogoutDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Unable to logout user');
      setError(err.message || 'Unable to logout user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleActivateSubscription = async () => {
    if (!selectedUser || !subscriptionForm.price) {
      toast.error('Please enter subscription price');
      return;
    }
    setFormLoading(true);
    setError('');
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + parseInt(subscriptionForm.durationMonths));
      
      // Format dates to MySQL datetime format
      const formatDateTime = (date) => {
        return date.toISOString().slice(0, 19).replace('T', ' ');
      };
      
      const updateData = {
        subscription_status: 'active',
        subscription_start_date: formatDateTime(startDate),
        subscription_end_date: formatDateTime(endDate),
        subscription_price: parseFloat(subscriptionForm.price),
        payment_type: subscriptionForm.paymentType || 'Not specified',
        searches_used_today: 0,
        emails_sent_today: 0,
        ai_emails_generated_today: 0,
        // Set unlimited limits for active subscriptions
        quota_per_day: -1, // -1 means unlimited
        email_daily_limit: -1,
        smtp_accounts_limit: -1,
      };
      
      // Only add reseller_name if it has a value
      if (subscriptionForm.resellerName && subscriptionForm.resellerName.trim()) {
        updateData.reseller_name = subscriptionForm.resellerName.trim();
      }
      
      console.log('📤 Sending subscription update:', {
        userId: selectedUser.id,
        data: updateData,
        hasToken: !!token
      });
      
      await adminApi.updateUser(selectedUser.id, updateData, token);
      
      toast.success(`Subscription activated for ${selectedUser.email}!`);
      setActivateSubscriptionDialogOpen(false);
      setSubscriptionForm({ price: '', paymentType: '', durationMonths: 1, resellerName: '' });
      fetchData();
    } catch (err) {
      console.error('❌ Subscription activation error:', err);
      const errorMessage = err.message || 'Unable to activate subscription';
      toast.error(errorMessage);
      setError(errorMessage);
      
      // If unauthorized, refresh the page to re-authenticate
      if (err.status === 401 || err.status === 403) {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info('Copied to clipboard!');
  };

  const copyAccountDetails = (user) => {
    const expiryDate = user.subscription_end_date 
      ? new Date(user.subscription_end_date).toLocaleDateString()
      : 'No expiry';
    
    const details = `
━━━━━━━━━━━━━━━━━━━━━
🎉 Account Details 🎉
━━━━━━━━━━━━━━━━━━━━━

📧 Email: ${user.email}
🔐 Password: [Contact Admin]
💰 Price: Rs. ${user.subscription_price || 0}
📅 Expiry Date: ${expiryDate}
✅ Status: ${user.subscription_status || 'trial'}
${user.reseller_name ? `👤 Sold by: ${user.reseller_name}` : ''}

━━━━━━━━━━━━━━━━━━━━━
Thank you for your purchase!
━━━━━━━━━━━━━━━━━━━━━
    `.trim();
    
    navigator.clipboard.writeText(details);
    toast.success('Account details copied!', { 
      title: 'Ready to send to customer' 
    });
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditForm({
      email: userToEdit.email,
      quota_per_day: userToEdit.quota_per_day || 250,
      role: userToEdit.role || 'user',
      is_active: userToEdit.is_active !== false,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (userToDelete) => {
    setSelectedUser(userToDelete);
    setDeleteDialogOpen(true);
  };

  const openResetPasswordDialog = (userToReset) => {
    setSelectedUser(userToReset);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // If not authenticated or not admin, useEffect will redirect to home page
  // This is just a fallback loading state during redirect
  if (!isAuthenticated || (user && user.role !== 'admin')) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Redirecting...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', py: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettings color="primary" sx={{ fontSize: 36 }} />
            Admin Portal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage users, subscriptions, sessions, monitor activity, and control system access
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<Person />}
            label={user?.email}
            color="primary"
            variant="outlined"
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" color="inherit" onClick={logout} startIcon={<Security />}>
            Logout
          </Button>
        </Stack>
      </Box>

      {/* Alerts */}
      <Collapse in={Boolean(error)}>
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      </Collapse>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<People />}
            color="#2563eb"
            subtitle="Registered accounts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Online Now"
            value={stats.onlineUsers}
            icon={<CheckCircle />}
            color="#10b981"
            subtitle="Active in 5 min"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="SMTP Accounts"
            value={stats.totalSmtpAccounts}
            icon={<MailOutline />}
            color="#8b5cf6"
            subtitle="Email accounts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Searches Today"
            value={stats.totalSearchesToday}
            icon={<Search />}
            color="#f59e0b"
            subtitle="API calls made"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Active Subscriptions"
            value={stats.activeSubscriptions}
            icon={<CheckCircle />}
            color="#10b981"
            subtitle="Paid users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Total Revenue"
            value={`Rs. ${stats.totalRevenue.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<TrendingUp />}
            color="#ec4899"
            subtitle="All subscriptions"
          />
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab
              icon={<Badge badgeContent={users.length} color="primary"><People /></Badge>}
              label="Users"
              iconPosition="start"
            />
            <Tab
              icon={<Badge badgeContent={sessions.length} color="info"><Security /></Badge>}
              label="Sessions"
              iconPosition="start"
            />
            <Tab
              icon={<Group />}
              label="Team Management"
              iconPosition="start"
            />
            <Tab
              icon={<MailOutline />}
              label="Tracking"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {loading && <LinearProgress />}

        <CardContent sx={{ p: 3 }}>
          {/* Users Tab */}
          {activeTab === 0 && (
            <>
              {/* Toolbar */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} justifyContent="space-between">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                    }}
                    sx={{ minWidth: 280 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Reseller</InputLabel>
                    <Select
                      value={resellerFilter}
                      label="Filter by Reseller"
                      onChange={(e) => setResellerFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Resellers</MenuItem>
                      <MenuItem value="none">No Reseller (Direct)</MenuItem>
                      {uniqueResellers.map(reseller => (
                        <MenuItem key={reseller} value={reseller}>
                          {reseller} ({stats.resellerCounts[reseller] || 0})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                    px: 3,
                  }}
                >
                  Add User
                </Button>
              </Stack>

              {/* Users Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subscription</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reseller</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Days Left</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Price (Rs.)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sessions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((u) => {
                        const online = isUserOnline(u.id);
                        const userSessions = getUserSessions(u.id);
                        const subStatus = getSubscriptionStatus(u);
                        const daysLeft = calculateDaysRemaining(u.subscription_end_date);
                        return (
                          <TableRow key={u.id} hover>
                            <TableCell>
                              <Tooltip title={online ? 'Online Now' : 'Offline'}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: online ? '#10b981' : '#94a3b8',
                                    boxShadow: online ? '0 0 8px #10b981' : 'none',
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ 
                                  bgcolor: u.role === 'admin' ? 'primary.main' : 
                                          u.role === 'manager' ? 'secondary.main' : 
                                          'grey.400' 
                                }}>
                                  {u.email?.[0]?.toUpperCase() || 'U'}
                                </Avatar>
                                <Box>
                                  <Typography fontWeight={600}>{u.email}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {u.id}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={u.role || 'user'}
                                color={u.role === 'admin' ? 'primary' : u.role === 'manager' ? 'secondary' : 'default'}
                                icon={
                                  u.role === 'admin' ? <AdminPanelSettings sx={{ fontSize: 16 }} /> : 
                                  u.role === 'manager' ? <People sx={{ fontSize: 16 }} /> :
                                  <Person sx={{ fontSize: 16 }} />
                                }
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={subStatus.label}
                                sx={{
                                  bgcolor: `${subStatus.color}20`,
                                  color: subStatus.color,
                                  fontWeight: 700,
                                  cursor: subStatus.status === 'trial' ? 'pointer' : 'default',
                                }}
                                onClick={() => {
                                  if (subStatus.status === 'trial' || subStatus.status === 'expired') {
                                    setSelectedUser(u);
                                    setActivateSubscriptionDialogOpen(true);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {u.reseller_name ? (
                                <Chip
                                  size="small"
                                  label={u.reseller_name}
                                  sx={{
                                    bgcolor: '#f59e0b20',
                                    color: '#f59e0b',
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">Direct</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {subStatus.status === 'active' ? (
                                <Typography fontWeight={600} color={daysLeft < 7 ? 'error.main' : 'text.primary'}>
                                  {daysLeft} days
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={600}>
                                {u.subscription_price ? `Rs. ${u.subscription_price}` : '-'}
                              </Typography>
                              {u.payment_type && (
                                <Typography variant="caption" color="text.secondary">
                                  {u.payment_type}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge badgeContent={userSessions.length} color="primary">
                                <Security sx={{ fontSize: 20, color: 'text.secondary' }} />
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={u.is_active !== false ? 'Active' : 'Disabled'}
                                color={u.is_active !== false ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {(subStatus.status === 'trial' || subStatus.status === 'expired') && (
                                  <Tooltip title="Activate Subscription">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => {
                                        setSelectedUser(u);
                                        setActivateSubscriptionDialogOpen(true);
                                      }}
                                    >
                                      <CheckCircle />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {online && (
                                  <Tooltip title="Force Logout">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => {
                                        setSelectedUser(u);
                                        setForceLogoutDialogOpen(true);
                                      }}
                                    >
                                      <Close />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {subStatus.status === 'active' && (
                                  <Tooltip title="Copy Account Details">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => copyAccountDetails(u)}
                                    >
                                      <ContentCopy />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <UserActionsMenu
                                  user={u}
                                  onEdit={openEditDialog}
                                  onDelete={openDeleteDialog}
                                  onToggleStatus={handleToggleStatus}
                                  onResetPassword={openResetPasswordDialog}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            {searchQuery || resellerFilter !== 'all' ? 'No users match your filters' : 'No users found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}

          {/* Sessions Tab */}
          {activeTab === 1 && (
            <Stack spacing={2}>
              {sessions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Security sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">No active sessions found</Typography>
                </Box>
              ) : (
                sessions.map((session) => (
                  <Card
                    key={session.id}
                    variant="outlined"
                    sx={{
                      borderColor: session.suspicious ? 'error.main' : 'divider',
                      bgcolor: session.suspicious ? 'error.lighter' : 'transparent',
                    }}
                  >
                    <CardContent>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: session.role === 'admin' ? 'primary.main' : 'grey.400' }}>
                            {session.email?.[0]?.toUpperCase() || 'U'}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600}>{session.email}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.ip_address || 'Unknown IP'} • {session.user_agent?.substring(0, 50) || 'Unknown device'}...
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            label={session.role}
                            color={session.role === 'admin' ? 'primary' : 'default'}
                          />
                          <Chip
                            size="small"
                            label={`Last seen: ${new Date(session.last_seen).toLocaleString()}`}
                            variant="outlined"
                            icon={<Schedule sx={{ fontSize: 14 }} />}
                          />
                          {session.suspicious ? (
                            <Chip
                              size="small"
                              label={session.warning || 'Suspicious'}
                              color="error"
                              icon={<Warning sx={{ fontSize: 14 }} />}
                            />
                          ) : (
                            <Chip size="small" label="Normal" color="success" variant="outlined" />
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          )}

          {/* Team Management Tab */}
          {activeTab === 2 && (
            <AdminTeamManagement />
          )}

          {activeTab === 3 && (
            <Stack spacing={3}>
              <Alert severity="info">
                The analyzed Chrome extension is MailSuite/Mailtrack. It depends on the vendor's hosted service at `mailsuite.com` and cannot be embedded as a self-hosted SMTP tracker inside this app.
              </Alert>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Email Tracking Provider
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose whether email sends use no tracking, the legacy in-app tracker, or the external MailSuite account connection.
                      </Typography>
                    </Box>

                    <FormControl fullWidth>
                      <InputLabel>Provider</InputLabel>
                      <Select
                        value={trackingProvider}
                        label="Provider"
                        onChange={(e) => setTrackingProvider(e.target.value)}
                      >
                        <MenuItem value="none">Disabled</MenuItem>
                        <MenuItem value="internal">Internal legacy tracking</MenuItem>
                        <MenuItem value="mailsuite">MailSuite / Mailtrack</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!trackingIntegration.enabled}
                          onChange={(e) =>
                            setTrackingIntegration((prev) => ({ ...prev, enabled: e.target.checked }))
                          }
                        />
                      }
                      label="Connection marked active"
                    />

                    <TextField
                      fullWidth
                      label="MailSuite account email"
                      value={trackingIntegration.accountEmail || ''}
                      onChange={(e) =>
                        setTrackingIntegration((prev) => ({ ...prev, accountEmail: e.target.value }))
                      }
                      helperText="Reference account used by the admin in the MailSuite extension/dashboard."
                    />

                    <TextField
                      fullWidth
                      label="Dashboard URL"
                      value={trackingIntegration.dashboardUrl || 'https://mailsuite.com'}
                      onChange={(e) =>
                        setTrackingIntegration((prev) => ({ ...prev, dashboardUrl: e.target.value }))
                      }
                    />

                    <TextField
                      fullWidth
                      label="Send webhook URL"
                      value={trackingIntegration.sendWebhookUrl || ''}
                      onChange={(e) =>
                        setTrackingIntegration((prev) => ({ ...prev, sendWebhookUrl: e.target.value }))
                      }
                      helperText="Paste the Zapier Catch Hook URL that forwards send requests into MailSuite Send Email."
                    />

                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Integration notes"
                      value={trackingIntegration.notes || ''}
                      onChange={(e) =>
                        setTrackingIntegration((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      helperText="Document login steps, browser profile requirements, or team instructions."
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        fullWidth
                        label="Webhook secret"
                        value={trackingIntegration.webhookSecret || ''}
                        onChange={(e) =>
                          setTrackingIntegration((prev) => ({ ...prev, webhookSecret: e.target.value }))
                        }
                        helperText="Use this secret in the MailSuite-to-app webhook."
                      />
                      <Button variant="outlined" onClick={generateTrackingWebhookSecret}>
                        Generate Secret
                      </Button>
                    </Stack>

                    <TextField
                      fullWidth
                      label="App webhook URL"
                      value={trackingWebhookUrl}
                      InputProps={{ readOnly: true }}
                      helperText="Use this URL in Zapier Webhooks or any MailSuite-compatible forwarder."
                    />

                    <Alert severity="info">
                      Official MailSuite support found: Zapier-based actions and first-open notifications. Practical setup is:
                      sign in to MailSuite in its own dashboard, connect it to Zapier, then forward first-open events to this webhook URL.
                    </Alert>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        onClick={handleSaveTrackingIntegration}
                        disabled={trackingSaving}
                      >
                        {trackingSaving ? 'Saving...' : 'Save Tracking Settings'}
                      </Button>
                      <Button
                        variant="outlined"
                        href={trackingIntegration.dashboardUrl || 'https://mailsuite.com'}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open MailSuite Login
                      </Button>
                    </Stack>

                    <Alert severity={trackingProvider === 'mailsuite' && trackingIntegration.enabled ? 'success' : 'warning'}>
                      {trackingProvider === 'mailsuite' && trackingIntegration.enabled
                        ? 'MailSuite mode is active. App-side legacy pixel tracking will stay disabled when emails are sent from Email Studio.'
                        : 'If MailSuite is not active, emails from this app will not use the MailSuite extension. Browser-extension tracking only works inside the vendor flow, mainly on Gmail.'}
                    </Alert>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}

          {/* Subscriptions Tab */}
        </CardContent>
      </Card>

      {/* Activate Subscription Dialog */}
      <Dialog open={activateSubscriptionDialogOpen} onClose={() => setActivateSubscriptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircle color="success" />
            <span>Activate Subscription</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info">
              Activating subscription for: <strong>{selectedUser?.email}</strong>
            </Alert>

            <TextField
              fullWidth
              label="Subscription Price (Rs.)"
              type="number"
              value={subscriptionForm.price}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, price: e.target.value })}
              required
              helperText="Enter the amount customer paid"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>Rs.</Typography>,
              }}
            />

            <TextField
              fullWidth
              label="Payment Type"
              value={subscriptionForm.paymentType}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, paymentType: e.target.value })}
              placeholder="Cash / Bank Transfer / JazzCash / EasyPaisa"
              helperText="How did customer pay?"
            />

            <TextField
              fullWidth
              label="Reseller Name (Optional)"
              value={subscriptionForm.resellerName}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, resellerName: e.target.value })}
              placeholder="Enter reseller name"
              helperText="Who sold this subscription? Leave empty for direct sales"
            />

            <TextField
              fullWidth
              select
              label="Duration"
              value={subscriptionForm.durationMonths}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, durationMonths: e.target.value })}
            >
              <MenuItem value={1}>1 Month</MenuItem>
              <MenuItem value={3}>3 Months</MenuItem>
              <MenuItem value={6}>6 Months</MenuItem>
              <MenuItem value={12}>12 Months</MenuItem>
            </TextField>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Subscription Details</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Start Date:</Typography>
                  <Typography variant="body2" fontWeight={600}>{new Date().toLocaleDateString()}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">End Date:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(new Date().setMonth(new Date().getMonth() + parseInt(subscriptionForm.durationMonths || 1))).toLocaleDateString()}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700}>Total Amount:</Typography>
                  <Typography variant="body2" fontWeight={700} color="primary">Rs. {subscriptionForm.price || 0}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleActivateSubscription}
            disabled={!subscriptionForm.price || formLoading}
          >
            {formLoading ? 'Activating...' : 'Activate Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Logout Dialog */}
      <Dialog open={forceLogoutDialogOpen} onClose={() => setForceLogoutDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Force Logout</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to log out <strong>{selectedUser?.email}</strong> from all devices?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will terminate all active sessions for this user.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceLogoutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleForceLogout} disabled={formLoading}>
            Force Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Add color="primary" />
            <span>Create New User</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            margin="dense"
            value={createForm.email}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
            }}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="dense"
            value={createForm.password}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start"><Security /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Daily Search Quota"
            type="number"
            fullWidth
            margin="dense"
            value={createForm.quota_per_day}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, quota_per_day: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={createForm.role}
              label="Role"
              onChange={(e) => setCreateForm((prev) => ({ ...prev, role: e.target.value }))}
            >
              <MenuItem value="user">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Person sx={{ fontSize: 20 }} />
                  <span>User</span>
                </Stack>
              </MenuItem>
              <MenuItem value="manager">
                <Stack direction="row" spacing={1} alignItems="center">
                  <People sx={{ fontSize: 20 }} />
                  <span>Manager</span>
                </Stack>
              </MenuItem>
              <MenuItem value="admin">
                <Stack direction="row" spacing={1} alignItems="center">
                  <AdminPanelSettings sx={{ fontSize: 20 }} />
                  <span>Admin</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={formLoading || !createForm.email || !createForm.password}
          >
            {formLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Edit color="primary" />
            <span>Edit User</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            margin="dense"
            value={editForm.email}
            onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
            disabled
          />
          <TextField
            label="Daily Search Quota"
            type="number"
            fullWidth
            margin="dense"
            value={editForm.quota_per_day}
            onChange={(e) => setEditForm((prev) => ({ ...prev, quota_per_day: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={editForm.role}
              label="Role"
              onChange={(e) => setEditForm((prev) => ({ ...prev, role: e.target.value }))}
            >
              <MenuItem value="user">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Person sx={{ fontSize: 20 }} />
                  <span>User</span>
                </Stack>
              </MenuItem>
              <MenuItem value="manager">
                <Stack direction="row" spacing={1} alignItems="center">
                  <People sx={{ fontSize: 20 }} />
                  <span>Manager</span>
                </Stack>
              </MenuItem>
              <MenuItem value="admin">
                <Stack direction="row" spacing={1} alignItems="center">
                  <AdminPanelSettings sx={{ fontSize: 20 }} />
                  <span>Admin</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={editForm.is_active}
                onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
            }
            label="Account Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditUser} disabled={formLoading}>
            {formLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Delete color="error" />
            <span>Delete User</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedUser?.email}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} disabled={formLoading}>
            {formLoading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Security color="primary" />
            <span>Reset Password</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for <strong>{selectedUser?.email}</strong>
          </Typography>
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="dense"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <Tooltip title="Copy">
                    <IconButton onClick={() => copyToClipboard(newPassword)} edge="end">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" size="small" onClick={generatePassword} sx={{ mt: 1 }}>
            Generate Strong Password
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={formLoading || !newPassword}>
            {formLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPortal;
